/**
 * aligned's `purchase_orders` customer billing documents. This first port is
 * the read/display subset used by the admin Customers tool.
 */
export type PurchaseOrderStatus = 'active' | 'expired' | 'cancelled' | 'archived';

export interface PurchaseOrder {
  id: string;
  customerId: string;
  poNumber: string | null;
  vendorName: string | null;
  maxSpendMicroUsd: number | null;
  currency: string;
  startDate: string | null;
  endDate: string | null;
  paymentTerms: string | null;
  markdownBody: string | null;
  documentStoragePath: string | null;
  documentDownloadUrl: string | null;
  documentFileName: string | null;
  notes: string | null;
  status: PurchaseOrderStatus;
  uploadedAt: string | null;
  uploadedByFirebaseId: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

type Data = Record<string, unknown>;

function str(v: unknown): string | null {
  return typeof v === 'string' ? v : null;
}

function numOrNull(v: unknown): number | null {
  return typeof v === 'number' ? v : null;
}

function isoString(v: unknown): string | null {
  if (typeof v === 'string') return v;
  if (v instanceof Date) return Number.isNaN(v.getTime()) ? null : v.toISOString();
  if (v && typeof v === 'object' && 'toDate' in v && typeof (v as { toDate: unknown }).toDate === 'function') {
    const date = (v as { toDate: () => Date }).toDate();
    return Number.isNaN(date.getTime()) ? null : date.toISOString();
  }
  return null;
}

function status(v: unknown): PurchaseOrderStatus {
  return v === 'expired' || v === 'cancelled' || v === 'archived' ? v : 'active';
}

export function mapPurchaseOrder(id: string, data: Data): PurchaseOrder {
  return {
    id,
    customerId: str(data.customerId) ?? '',
    poNumber: str(data.poNumber),
    vendorName: str(data.vendorName),
    maxSpendMicroUsd: numOrNull(data.maxSpendMicroUsd),
    currency: str(data.currency) ?? 'USD',
    startDate: isoString(data.startDate),
    endDate: isoString(data.endDate),
    paymentTerms: str(data.paymentTerms),
    markdownBody: str(data.markdownBody),
    documentStoragePath: str(data.documentStoragePath),
    documentDownloadUrl: str(data.documentDownloadUrl),
    documentFileName: str(data.documentFileName),
    notes: str(data.notes),
    status: status(data.status),
    uploadedAt: isoString(data.uploadedAt),
    uploadedByFirebaseId: str(data.uploadedByFirebaseId),
    createdAt: isoString(data.createdAt),
    updatedAt: isoString(data.updatedAt),
  };
}
